import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import Select from 'react-select';
import 'react-quill/dist/quill.snow.css';
import { useSidebar } from '../SidebarContext';
import ImageUploading from 'react-images-uploading';
import { X } from 'lucide-react';
import { toast } from "react-toastify"; // ✅ Only toast is needed



const propertyTypes = [
    { label: 'Standard Rooms', value: 'standard' },
    { label: 'Deluxe Rooms', value: 'deluxe' },
    { label: 'Family Rooms', value: 'family' },
    { label: 'Business Rooms', value: 'business' },
    { label: 'Luxury Suites', value: 'luxury' },
];



const NewRoom = ({ editRoomData }) => {
    const [mainImage, setMainImage] = useState([]);
    const [galleryImages, setGalleryImages] = useState([]);
    const [keyFeaturesOptions, setKeyFeaturesOptions] = useState([]);
    const [guestPreferencesOptions, setGuestPreferencesOptions] = useState([]);
    const [amenitiesOptions, setAmenitiesOptions] = useState([]);


    const BASE_URL = process.env.REACT_APP_API_BASE_URL;


    const { isSidebarOpen } = useSidebar();
    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm();

    // const BASE_URL = process.env.REACT_APP_API_BASE_URL;


    const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
    const [additionalCharge, setAdditionalCharge] = useState(
        localStorage.getItem('additionalCharge') || ''
    );
    const [gstPercentage, setGstPercentage] = useState(
        localStorage.getItem('gstPercentage') || ''
    );

    useEffect(() => {
        localStorage.setItem('additionalCharge', additionalCharge);
        localStorage.setItem('gstPercentage', gstPercentage);
    }, [additionalCharge, gstPercentage]);

    useEffect(() => {
        const fetchKeyFeatures = async () => {
            try {
                const res = await fetch(`${BASE_URL}/api/key-features`);
                if (!res.ok) throw new Error("Failed to fetch key features");

                const data = await res.json();
                const formattedOptions = data.map((item) => ({
                    value: String(item.id), // convert to string
                    label: item.name,
                }));

                setKeyFeaturesOptions(formattedOptions);
            } catch (err) {
                console.error("Error fetching key features:", err);
            }
        };

        fetchKeyFeatures();
    }, []);

    useEffect(() => {
        const fetchGuestPreferences = async () => {
            try {
                const res = await fetch(`${BASE_URL}/api/guest-preferences`);
                if (!res.ok) throw new Error("Failed to fetch guest preferences");

                const data = await res.json();
                const formatted = data.map((item) => ({
                    value: String(item.id),   // change here
                    label: item.name,
                }));
                setGuestPreferencesOptions(formatted);
            } catch (err) {
                console.error("Error fetching guest preferences:", err);
            }
        };

        fetchGuestPreferences();
    }, []);

    useEffect(() => {
        const fetchAmenities = async () => {
            try {
                const res = await fetch(`${BASE_URL}/api/amenity`);
                if (!res.ok) throw new Error("Failed to fetch amenities");

                const data = await res.json();
                const formatted = data.map((item) => ({
                    value: String(item.id),   // change here
                    label: item.name,
                }));
                setAmenitiesOptions(formatted);
            } catch (err) {
                console.error("Error fetching amenities:", err);
            }
        };

        fetchAmenities();
    }, []);

    useEffect(() => {
        if (
            !editRoomData ||
            !keyFeaturesOptions.length ||
            !amenitiesOptions.length ||
            !guestPreferencesOptions.length
        ) {
            return;
        }

        // Helper: map array of IDs to full select objects
        const mapIdsToOptions = (ids, options) => {
            return (ids || []).map((id) => options.find((opt) => opt.value === id)).filter(Boolean);
        };

        reset({
            ...editRoomData,
            propertyType: propertyTypes.find((p) => p.value === editRoomData.propertyType),

            keyFeatures: keyFeaturesOptions.filter(opt =>
                (editRoomData.keyFeatures || []).includes(opt.label)
            ),
            amenities: amenitiesOptions.filter(opt =>
                (editRoomData.amenities || []).includes(opt.label)
            ),
            guestPreferences: guestPreferencesOptions.filter(opt =>
                (editRoomData.guestPreferences || []).includes(opt.label)
            ),
        });

        setMainImage(
            editRoomData.mainImage
                ? [
                    {
                        data_url: editRoomData.mainImage.startsWith('http')
                            ? editRoomData.mainImage
                            : `${BASE_URL}${editRoomData.mainImage}`,
                    },
                ]
                : []
        );

        // Set Gallery Images
        setGalleryImages(
            (editRoomData.galleryImages || []).map((url) => ({
                data_url: url.startsWith('http') ? url : `${BASE_URL}${url}`,
            }))
        );

        setAdditionalCharge(editRoomData.additionalCharge || '');
        setGstPercentage(editRoomData.gstPercentage || '');
        setShowAdditionalCharges(
            !!editRoomData.additionalCharge || !!editRoomData.gstPercentage
        );
    }, [
        editRoomData,
        reset,
        keyFeaturesOptions,
        amenitiesOptions,
        guestPreferencesOptions,
    ]);



    const stripHtml = (html) => {
        const tmp = document.createElement("DIV");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
    };


    const onSubmit = async (formData) => {
        const payload = {
            // Basic Info
            title: formData.title,
            description: stripHtml(formData.description),  // ✅ Convert to plain text here
            address: formData.address,
            city: formData.city,
            state: formData.state,

            // Prices
            originalPrice: Number(formData.originalPrice) || 0,
            discountedPrice: Number(formData.discountedPrice) || 0,
            additionalCharge: Number(additionalCharge) || 0,
            gstPercentage: Number(gstPercentage) || 0,

            // Property Type
            propertyType: formData.propertyType?.value || null,

            // Features, Amenities, Preferences
            keyFeatures: (formData.keyFeatures || [])
                .filter(item => item && item.label)
                .map(item => item.label),

            amenities: (formData.amenities || [])
                .filter(item => item && item.label)
                .map(item => item.label),

            guestPreferences: (formData.guestPreferences || [])
                .filter(item => item && item.label)
                .map(item => item.label),


            // Room Rules
            roomRules: stripHtml(formData.roomRules || ""),  // ✅ Strip roomRules too if needed

            // Images
            // mainImage: mainImage[0]?.data_url || null,
            // galleryImages: galleryImages.map((img) => img.data_url),

            mainImage:
                mainImage.length > 0
                    ? mainImage[0].data_url?.startsWith('data:image')  // ✅ check for base64
                        ? mainImage[0].data_url  // newly uploaded image (base64)
                        : mainImage[0].data_url.replace(BASE_URL, '')  // existing image path
                    : null,


            galleryImages: galleryImages
                .filter(img => img?.data_url)
                .map(img =>
                    img.data_url.startsWith(BASE_URL)
                        ? img.data_url.replace(BASE_URL, "")
                        : img.data_url
                ),



        };

        const isEditMode = !!editRoomData;
        const url = isEditMode
            ? `${BASE_URL}/api/rooms/${editRoomData.id}`
            : `${BASE_URL}/api/rooms`;
        const method = isEditMode ? "PUT" : "POST";

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`Server error: ${errorText}`);
            }

            const result = await res.json();
                    toast.success(isEditMode ? "Room updated successfully!" : "Room created successfully!"); // ✅ SUCCESS TOAST

              

            console.log(result);
                     setTimeout(() => {
        window.close(); 
    }, 2000);
        } catch (err) {
            console.error(err);
            toast.error("Submission failed.");
        }
    };






    return (
        <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
            <form className="nrp-form" onSubmit={handleSubmit(onSubmit)}>
                <h2 className="nrp-title">New Room Posting</h2>

                {/* Title */}
                <div className="nrp-group">
                    <label className="nrp-label">Room Title</label>
                    <input
                        type="text"
                        placeholder="Enter room title"
                        {...register('title', { required: true })}
                        className="nrp-input"
                    />
                </div>

                {/* Description */}
                <div className="nrp-group">
                    <label className="nrp-label">Room Description</label>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <ReactQuill {...field} className="nrp-quill" placeholder="Write a detailed room description..." />
                        )}
                    />
                </div>

                {/* Address Section */}
                <div className="nrp-row">
                    <div className="nrp-group">
                        <label className="nrp-label">Address</label>
                        <input
                            type="text"
                            placeholder="123 Street Name"
                            {...register('address')}
                            className="nrp-input"
                        />
                    </div>
                    <div className="nrp-group">
                        <label className="nrp-label">City</label>
                        <input
                            type="text"
                            placeholder="City name"
                            {...register('city')}
                            className="nrp-input"
                        />
                    </div>
                    <div className="nrp-group">
                        <label className="nrp-label">State</label>
                        <input
                            type="text"
                            placeholder="State/Province"
                            {...register('state')}
                            className="nrp-input"
                        />
                    </div>
                </div>

                {/* Price */}
                <div className="nrp-row">
                    <div className="nrp-group">
                        <label className="nrp-label">Original Price</label>
                        <input
                            type="number"
                            placeholder="e.g. 5000"
                            {...register('originalPrice')}
                            className="nrp-input"
                        />
                    </div>
                    <div className="nrp-group">
                        <label className="nrp-label">Discounted Price</label>
                        <input
                            type="number"
                            placeholder="e.g. 4500"
                            {...register('discountedPrice')}
                            className="nrp-input"
                        />
                    </div>
                </div>

                <div className="nrp-group">
                    <label className="nrp-label">Main Room Image</label>
                    <ImageUploading
                        multiple={false}
                        value={mainImage}
                        onChange={(imageList) => setMainImage(imageList)}
                        maxNumber={1}
                        dataURLKey="data_url"
                    >
                        {({ imageList, onImageUpload, onImageRemove }) => (
                            <div className="nrp-image-upload">
                                {imageList.length === 0 ? (
                                    <button type="button" className="nrp-btn-sm" onClick={onImageUpload}>
                                        Upload Image
                                    </button>
                                ) : (
                                    <div className="nrp-preview-box">
                                        <img src={imageList[0]['data_url']} alt="Main Preview" className="nrp-preview-img" />
                                        <button type="button" className="nrp-remove-img" onClick={() => onImageRemove(0)}>
                                            <X />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </ImageUploading>
                </div>

                <div className="nrp-group">
                    <label className="nrp-label">Gallery Images</label>
                    <ImageUploading
                        multiple
                        value={galleryImages}
                        onChange={(imageList) => setGalleryImages(imageList)}
                        maxNumber={10}
                        dataURLKey="data_url"
                    >
                        {({ imageList, onImageUpload, onImageRemoveAll, onImageUpdate, onImageRemove }) => (
                            <div className="nrp-image-upload">
                                <button type="button" className="nrp-btn-sm" onClick={onImageUpload}>
                                    Upload Images
                                </button>
                                {imageList.length > 0 && (
                                    <button type="button" className="nrp-btn-sm danger ml-2" onClick={onImageRemoveAll}>
                                        Remove All
                                    </button>
                                )}

                                <div className="nrp-gallery-grid">
                                    {imageList.map((image, index) => (
                                        <div key={index} className="nrp-preview-box">
                                            <img src={image['data_url']} alt="" className="nrp-preview-img" />
                                            <div className="nrp-btn-group">
                                                <button type="button" className="nrp-remove-img" onClick={() => onImageRemove(index)}><X /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </ImageUploading>
                </div>



                {/* Key Features */}
                <div className="nrp-group">
                    <label className="nrp-label">Key Features</label>
                    <Controller
                        name="keyFeatures"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                options={keyFeaturesOptions}
                                isMulti
                                className="nrp-select-multi"
                                classNamePrefix="nrp-select"
                                placeholder="Select Key Features"
                                value={field.value || []}
                                onChange={(selected) => field.onChange(selected)}


                            />
                        )}
                    />

                </div>

                {/* Amenities */}
                <div className="nrp-group">
                    <label className="nrp-label">Amenities</label>
                    <Controller
                        name="amenities"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                options={amenitiesOptions}
                                isMulti
                                className="nrp-select-multi"
                                classNamePrefix="nrp-select"
                                placeholder="Select Amenities"
                                value={field.value || []}
                                onChange={(selected) => field.onChange(selected)}

                            />
                        )}
                    />

                </div>


                {/* Room Rules */}
                <div className="nrp-group">
                    <label className="nrp-label">Room Rules</label>
                    <Controller
                        name="roomRules"
                        control={control}
                        render={({ field }) => (
                            <ReactQuill {...field} className="nrp-quill" placeholder="Specify guest rules, e.g. no smoking..." />
                        )}
                    />
                </div>

                <div className="nrp-group">
                    <label className="nrp-label">Guest Preferences</label>
                    <Controller
                        name="guestPreferences"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                options={guestPreferencesOptions}
                                isMulti
                                className="nrp-select-multi"
                                classNamePrefix="nrp-select"
                                placeholder="Select Guest Preferences"
                                value={field.value || []}
                                onChange={(selected) => field.onChange(selected)}

                            />
                        )}
                    />
                </div>

                {/* Property Type */}
                <div className="nrp-group">
                    <label className="nrp-label">Property Type</label>
                    <Controller
                        name="propertyType"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                options={propertyTypes}
                                placeholder="Select property type"
                                className="nrp-select"
                            />
                        )}
                    />
                </div>

                <div className="nrp-group">
                    <label className="nrp-label">
                        <input
                            type="checkbox"
                            checked={showAdditionalCharges}
                            onChange={() => setShowAdditionalCharges(!showAdditionalCharges)}
                        />{' '}
                        Include Additional Charges
                    </label>

                    {showAdditionalCharges && (
                        <div className="nrp-additional-box nrp-row">
                            <div className="nrp-group">
                                <label className="nrp-label">Hotel Service Charge</label>
                                <input
                                    type="number"
                                    className="nrp-input"
                                    placeholder="e.g. 200"
                                    value={additionalCharge}
                                    onChange={(e) => setAdditionalCharge(e.target.value)}
                                />
                            </div>
                            <div className="nrp-group">
                                <label className="nrp-label">GST (%)</label>
                                <input
                                    type="number"
                                    className="nrp-input"
                                    placeholder="e.g. 18"
                                    value={gstPercentage}
                                    onChange={(e) => setGstPercentage(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>


                {/* Submit */}
                <button type="submit" className="nrp-btn">
                    Submit Room
                </button>


            </form>

        </div>

    );

};

export default NewRoom;
